import React, { useState } from 'react';
import { useAuth } from '../contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Flag, X, AlertTriangle, Copyright, Trash, Eye, HelpCircle, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { resolveThumbnailUrl } from '../utils/hlsPlayer';

const REPORT_CATEGORIES = [
  { id: 'inappropriate', label: 'Inappropriate Content', icon: AlertTriangle, description: 'Nudity, violence, or harmful content' },
  { id: 'copyright', label: 'Copyright Violation', icon: Copyright, description: 'Uses copyrighted material without permission' },
  { id: 'mislabeled_ai', label: 'Mislabeled AI Disclosure', icon: Sparkles, description: 'AI disclosure looks wrong or missing' },
  { id: 'spam', label: 'Spam or Misleading', icon: Trash, description: 'Misleading title, spam, or scam' },
  { id: 'misleading', label: 'Misleading Information', icon: Eye, description: 'False or deceptive information' },
  { id: 'other', label: 'Other', icon: HelpCircle, description: 'Other issues not listed above' },
];

export const ReportModal = ({ isOpen, onClose, video }) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const { axiosInstance } = useAuth();

  const handleSubmit = async () => {
    if (!selectedCategory) {
      toast.error('Please select a category');
      return;
    }
    if (!reason.trim()) {
      toast.error('Please describe the issue');
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.post(`/videos/${video.id}/report`, {
        category: selectedCategory,
        reason: reason.trim()
      });
      toast.success('Report submitted. Thank you for helping keep AUDIVUE safe.');
      handleClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedCategory('');
    setReason('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md mx-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Flag className="w-5 h-5 text-yellow-500" />
            Report Video
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Video info */}
          <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
            <div className="w-16 h-10 rounded bg-zinc-700 overflow-hidden flex-shrink-0">
              {/* Shared resolver so Bunny videos (custom_thumbnail_url /
                  thumbnail_url) show properly in the report modal — the
                  old thumbnail_path-only check left the preview blank. */}
              {video && (
                <img
                  src={resolveThumbnailUrl(video)}
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">{video?.title}</p>
              <p className="text-xs text-zinc-400">{video?.artist}</p>
            </div>
          </div>

          {/* Category selection */}
          <div>
            <p className="text-sm text-zinc-400 mb-2">What's the issue?</p>
            <div className="space-y-2">
              {REPORT_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      selectedCategory === cat.id
                        ? 'border-yellow-500 bg-yellow-500/10'
                        : 'border-zinc-700 bg-zinc-800/50 hover:bg-zinc-800'
                    }`}
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 ${
                      selectedCategory === cat.id ? 'text-yellow-500' : 'text-zinc-400'
                    }`} />
                    <div className="text-left">
                      <p className={`text-sm font-medium ${
                        selectedCategory === cat.id ? 'text-yellow-500' : 'text-white'
                      }`}>{cat.label}</p>
                      <p className="text-xs text-zinc-500">{cat.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Reason textarea */}
          <div>
            <p className="text-sm text-zinc-400 mb-2">Describe the issue</p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Please provide details about why you're reporting this video..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-white placeholder:text-zinc-500 text-sm resize-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500/20"
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-zinc-500 text-right mt-1">{reason.length}/500</p>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleClose}
              variant="outline"
              className="flex-1 bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || !selectedCategory || !reason.trim()}
              className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Flag className="w-4 h-4 mr-2" />
                  Submit Report
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
